import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { sentenceCase } from 'change-case';

import {
  Avatar,
  Button,
  Card,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback } from 'react';
import Iconify from '../../components/Iconify';
import axiosClient from '../../api/AxiosConfig';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { RecruitService } from '../../Services/recruit.service';
import RecruitMoreMenu from '../../sections/@dashboard/recruit/RecruitMoreMenu';
import Label from '../../components/Label';
import { useLoadingContext } from '../../context/loadingContext';

const RECRUIT_TYPES = ['Toàn thời gian', 'Bán thời gian'];

const Recruit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [provider, setProvider] = React.useState([]);
  const [recruits, setRecruits] = React.useState([]);
  const [location, setLocation] = React.useState('');
  const [recruitType, setRecruitType] = React.useState('');
  const { handleStartLoading, handleStopLoading } = useLoadingContext()


  React.useEffect(() => {
    const getStore = async () => {
      handleStartLoading()
      const data = await axiosClient.get('/province');
      setProvider(data.data);
    };

    getStore().finally(handleStopLoading);
  }, []);

  React.useEffect(() => {
    getRecruit().finally(handleStopLoading);
  }, [location, recruitType]);

  const getRecruit = async () => {
    handleStartLoading()
    const recruitsData = await RecruitService.GetRecruit({
      location,
      recruitType,
    });
    setRecruits(recruitsData.data);
  };

  const handleNewClick = () => navigate('create');
  const handleEditClick = (slug) => {
    navigate(`/recruit/edit/${slug}`, {
      state: location,
    });
  };

  const handleDeleteClick = useCallback(
    (id) =>
      Swal.fire({
        customClass: {
          container: { zIndex: 999999 },
        },
        title: t('sure.title'),
        text: t('sure.delete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('sure.yes'),
        cancelButtonText: t('sure.no'),
      }).then((result) => {
        handleStartLoading()
        if (result)
          RecruitService.DeleteRecruit(id)
            .then(() => {
              setRecruits((prevState) => prevState.filter((state) => state._id !== id));
              Swal.fire('Success', 'Cập nhật thành công', 'success');
            })
            .catch((err) => Swal.fire('Error!', err.response.data, 'error'))
            .finally(handleStopLoading)
      }),
    []
  );

  const handleDisableClick = useCallback(
    (id) => {
      handleStartLoading()
      RecruitService.DisableRecruit(id)
        .then(() => {
          setRecruits((prevState) =>
            prevState.map((state) => {
              if (state._id === id) {
                state.status = 'Inactive';
              }
              return state;
            })
          );
          Swal.fire('Success', 'Cập nhật thành công', 'success');
        })
        .catch((err) => Swal.fire('Error!', err.response.data, 'error'))
        .finally(handleStopLoading);
    },
    []
  );

  const handleEnableClick = useCallback(
    (id) => {
      handleStartLoading()
      RecruitService.EnableRecruit(id)
        .then(() => {
          setRecruits((prevState) =>
            prevState.map((state) => {
              if (state._id === id) {
                state.status = 'Active';
              }
              return state;
            })
          );
          Swal.fire('Success', 'Cập nhật thành công', 'success');
        })
        .catch((err) => Swal.fire('Error!', err.response.data, 'error'))
        .finally(handleStopLoading);
    },
    []
  );

  return (
    <Page title="Tuyển dụng">
      <Container maxWidth={'xl'}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Danh sách bài tuyển dụng
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <FormControl sx={{ width: '268px' }} size="small">
              <InputLabel id="type">Loại lao động</InputLabel>
              <Select
                name="type"
                value={recruitType}
                onChange={(e) => setRecruitType(e.target.value)}
                label="Loại lao động"
              >
                <MenuItem value="">
                  <em>Tất cả</em>
                </MenuItem>
                {RECRUIT_TYPES?.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '268px' }} size="small">
              <InputLabel id="address">Địa điểm</InputLabel>
              <Select name="address" value={location} onChange={(e) => setLocation(e.target.value)} label="Địa điểm">
                <MenuItem value="">
                  <em>Tất cả</em>
                </MenuItem>
                {provider?.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleNewClick} startIcon={<Iconify icon="akar-icons:plus" />}>
              Thêm mới
            </Button>
          </Stack>
        </Stack>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={'60px'}>STT</TableCell>
                    <TableCell>Người tạo</TableCell>
                    <TableCell>Tiêu đề</TableCell>
                    <TableCell>Loại</TableCell>
                    <TableCell>Vị trí</TableCell>
                    <TableCell>Lương</TableCell>
                    <TableCell>Ngày tạo</TableCell>
                    <TableCell>Trạng thái</TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recruits.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                        Không có dữ liệu
                      </TableCell>
                    </TableRow>
                  )}
                  {recruits.length > 0 &&
                    recruits.map((recruit, index) => (
                      <TableRow key={recruit._id} hover>
                        <TableCell width={60} align="center">
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar src={recruit.author.avatar} />
                            <Typography>{recruit.author.username}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{recruit.title}</TableCell>
                        <TableCell>{recruit.recruitType}</TableCell>
                        <TableCell>{recruit.recruitLocation.name}</TableCell>
                        <TableCell>{recruit && Number(recruit.price) === 0 ? 'Thỏa thuận' : recruit.price}</TableCell>
                        <TableCell>{moment(recruit.createdAt).format('DD/MM/YYYY')}</TableCell>
                        <TableCell>
                          <Label variant="ghost" color={(recruit.status === 'Inactive' && 'error') || 'success'}>
                            {sentenceCase(recruit.status)}
                          </Label>
                        </TableCell>
                        <TableCell>
                          <RecruitMoreMenu
                            id={recruit._id}
                            item={recruit}
                            status={recruit.status}
                            onDisable={handleDisableClick}
                            onEnable={handleEnableClick}
                            onDeteleRecruit={handleDeleteClick}
                            onEditRecruit={handleEditClick}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
};

export default Recruit;
